import { Formik, useFormikContext  } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import Select from 'react-select';
import { Progress } from "react-sweet-progress";
import { uploadFile } from "react-s3";
import { config } from "./../../utils/index";
import { toast } from "react-toastify";
import BackButton from "../../component/BackButton.jsx";
import Loader from "../../component/Loader.jsx";
import { CompanyDetailReducer, AddCompanyReducer } from "../slice";
import ukCitiesRaw from '../../map.json';

const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
const SUPPORTED_FORMATS_POLICY = ['application/pdf'];
const FILE_SIZE = 10 * 1024 * 1024; // 10MB

function EditCompany() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [time, setTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const details = useSelector((state) => state.main.companyDetail);
  const [submitting, setSubmitting] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  const getDetails = useCallback(() => {
    setLoading(true);
    dispatch(
      CompanyDetailReducer({
        id: id,
      })
    ).then(() => {
      setLoading(false);
    });
  }, [dispatch, id]);
  const initialValues = {
    id: details?.id || "",
    company_name: details?.company_name || "",
    job_role: details?.job_role || "",
    trading_name: details?.trading_name || "",
    business_type: details?.business_type || "",
    pricing_structure: details?.pricing_structure || "",
    registration_number: details?.registration_number || "",
    vat_number: details?.vat_number || "",
    full_name: details?.full_name || "",
    address_line1: details?.address_line1 || "",
    address_line2: details?.address_line2 || "",
    city: details?.city || "",
    slug: details?.slug || "",
    county: details?.county || "",
    postcode: details?.postcode || "",
    country: details?.country || "",
    phone_number: details?.phone_number || "",
    email: details?.email || "",
    website: details?.website || "",
    logo: details?.logo || "",
    logo_preview: details?.logo || "",
    banner_preview: details?.banner || "",
    booking_cancellation_policy: details?.booking_cancellation_policy || "",
    services: details?.services || [],
    target_age:details?.target_age || "",
    no_of_weekly_bookings:details?.no_of_weekly_bookings || "",
    operating_hours:details?.operating_hours || "",
    brand_color:details?.brand_color || "",
    stripe_account_id:details?.stripe_account_id || "",
    notes:details?.notes || "",
  };
  
  useEffect(() => {
    if (id) {
      getDetails();
    }
  }, [getDetails, id]);

  // useEffect(() => {
  //   dispatch(FetchAreaCodeList({ page: 1, limit: 10000 }));
  // }, [dispatch]);

  useEffect(() => {
    let interval;
    if (isActive && time < 100) {
      interval = setInterval(() => {
        // if (time < 100)
        setTime((seconds) => (seconds < 99 ? seconds + 1 : seconds));
      }, 10);
    }
    return () => clearInterval(interval);
  }, [isActive, time]);
  
  const [countyCityMap, setCountyCityMap] = useState({});
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const countries = ['UK'];

  // First load the county map
  useEffect(() => {
    const map = {};
    ukCitiesRaw.forEach(entry => {
      const city = entry[0];
      const county = entry[5] || "Unknown";
      if (!map[county]) {
        map[county] = [];
      }
      if (!map[county].includes(city)) {
        map[county].push(city);
      }
    });
    setCountyCityMap(map);
  }, []);

    // Then restore form data after map is loaded
    useEffect(() => {
      if (Object.keys(countyCityMap).length > 0) {
        const saved = localStorage.getItem("companyForm");
        if (saved) {
          const data = JSON.parse(saved);
          if (data.county) {
            setSelectedCounty(data.county);
          }
          if (data.city) {
            setSelectedCity(data.city);
          }
        }
      }
    }, [countyCityMap]);
  
    const handleCountyChange = (e) => {
      const newCounty = e.target.value;
      setSelectedCounty(newCounty);
      setSelectedCity('');
      setManualCity('');
      setShowManualInput(false);
      //setFieldValue('county', selectedCounty);
    };
  
    const handleCityChange = (e) => {
      if (e.target.value === 'manual') {
        setSelectedCity('');
        setShowManualInput(true);
      } else {
        setSelectedCity(e.target.value);
        setShowManualInput(false);
      }
    };
    setTimeout(() => {
      setSelectedCounty(details.county)
    }, 3000);

  if (loading) {
    return (
      <article className="col-lg-9 articleClass">
        {" "}
        <Loader />
      </article>
    );
  }
  return (
    <article className="col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center col-rever">
          <BackButton />
          <div className="col-sm-12 col-md-12 col-lg-12 mb-3 text-center">
            <h3>{id ? "Edit" : "Add New"} Company</h3>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-sm-12">
            <Formik
              initialValues={initialValues}
              validationSchema={Yup.object({
                company_name: Yup.string().required("Required"),
                full_name: Yup.string().required("Required"),
                job_role: Yup.string().required("Required"),
                business_type: Yup.string().required("Required"),
                address_line1: Yup.string().required("Required"),
                logo: Yup
                    .mixed()
                    .nullable()
                    .notRequired()
                    .test("fileSize", "Logo must be less than 10MB", (value) => {
                      if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
                      return value[0]?.size <= FILE_SIZE;
                    })
                    .test("fileFormat", "Unsupported file format", (value) => {
                      if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
                      return SUPPORTED_FORMATS.includes(value[0]?.type);
                    }),
                booking_cancellation_policy: Yup
                .mixed()
                .nullable()
                .notRequired()
                .test("fileSize", "Booking cancellation policy must be less than 10MB", (value) => {
                  if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
                  return value[0].size <= FILE_SIZE;
                })
                .test("fileFormat", "Unsupported file format", (value) => {
                  if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
                  return SUPPORTED_FORMATS_POLICY.includes(value[0].type);
                }),
                city: Yup.string().required("Required"),
                county: Yup.string().required("Required"),
                postcode: Yup.string().required("Required"),
                country: Yup.string().required("Required"),
                phone_number: Yup.string().required("Required"),
                email: Yup.string().required("Required"),
                services: Yup.array().min(1, "At least one service must be selected"),
                operating_hours: Yup.string().required("Required"),
                brand_color: Yup.string().required("Required"),
                no_of_weekly_bookings: Yup.string().required("Required"),
                target_age: Yup.string().required("Required"),
                pricing_structure: Yup.string().required("Required"),
              })}
              enableReinitialize
              onSubmit={async (values) => {
                setSubmitting(true);

                let payload = {
                  id: values?.id,
                  company_name: values?.company_name,
                  trading_name: values?.trading_name,
                  full_name: values?.full_name,
                  job_role: values?.job_role,
                  business_type: values?.business_type,
                  registration_number: values?.registration_number,
                  vat_number: values?.vat_number,
                  address_line1: values?.address_line1,
                  address_line2: values?.address_line2,
                  city: values?.city,
                  slug: values?.slug,
                  county: values?.county,
                  postcode: values?.postcode,
                  country: values?.country,
                  phone_number: values?.phone_number,
                  email: values?.email,
                  website: values?.website,
                  logo: selectedLogo,
                  banner: selectedBanner,
                  booking_cancellation_policy: selectedPolicy,
                  notes: values?.notes,
                  services: values?.services,
                  no_of_weekly_bookings: values?.no_of_weekly_bookings,
                  target_age: values?.target_age,
                  pricing_structure: values?.pricing_structure,
                  brand_color: values?.brand_color,
                  stripe_account_id: values?.stripe_account_id,
                };
                
                if (payload.city === "manual") {
                  payload.city = manualCity;
                }
          
                payload.services = JSON.stringify(values.services);
                  
                dispatch(
                  AddCompanyReducer(payload)
                ).then((res) => {
                  setSubmitting(false);

                  if (res?.payload?.code === 1) {
                    toast.success(res?.payload?.message);
                      navigate("/company", { replace: true });
                  } else {
                    toast?.error(res?.payload?.message);
                  }
                });
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFormikState,
                setFieldValue,
                /* and other goodies */
              }) => (
                <form className="form-style" onSubmit={handleSubmit}>
                  <input type="hidden" name="id" value={values.id} />
                  <div className="row">
                  <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group text-center">
                      <label className="fw-bold">Banner</label>
                        <div className="upload-btn-wrapper" style={{ borderRadius: '0px' }}>
                          <button className="btn">
                          <img
                                src={
                                  values.banner_preview
                                    ? values.banner_preview
                                    : "assets/images/upload-img.png"
                                }
                                alt=""
                                style={{ maxWidth: '100%', maxHeight: '200px' }}
                              />
                          </button>
                          <input
                            type="file"
                            accept="image/*"
                            name="banner"
                            onChange={(e) => {
                              if (e.target.files.length > 0) {
                                const file = e.target.files[0];
                                setSelectedBanner(file);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setFieldValue('banner_preview', reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </div>
                      </div>
                      {errors.banner && touched.banner && (
                        <div className="text-red">{errors.banner}</div>
                      )}
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group text-center">
                      <label className="fw-bold">Logo</label>
                        <div className="upload-btn-wrapper">
                          <button className="btn">
                          <img
                                src={
                                  values.logo_preview
                                    ? values.logo_preview
                                    : "assets/images/upload-img.png"
                                }
                                alt=""
                                style={{ maxWidth: '100%', maxHeight: '200px' }}
                              />
                          </button>
                          <input
                            type="file"
                            accept="image/*"
                            name="logo"
                            onChange={(e) => {
                              if (e.target.files.length > 0) {
                                const file = e.target.files[0];
                                setSelectedLogo(file);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setFieldValue('logo_preview', reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </div>
                      </div>
                      {errors.logo && touched.logo && (
                        <div className="text-red">{errors.logo}</div>
                      )}
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <label className="fw-bold">Company Name</label>
                      {errors.company_name && touched.company_name && (
                        <span className="text-red ms-1">{errors.company_name}</span>
                      )}
                      <div className="form-group">
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="company_name"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.company_name}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Trading Name</label>
                        {errors.trading_name && touched.trading_name && (
                          <span className="text-red ms-1">
                            {errors.trading_name}
                          </span>
                        )}
                        <div className="input-container">
                        <input
                            type="text"
                            className="form-control pl-5"
                            name="trading_name"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.trading_name}
                          />
                          <img
                            src="assets/images/address.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">VAT Number</label>
                        {errors.vat_number && touched.vat_number && (
                          <span className="text-red ms-1">
                            {errors.vat_number}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="vat_number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.vat_number}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Business Type</label>
                        {errors.business_type && touched.business_type && (
                          <span className="text-red ms-1">
                            {errors.business_type}
                          </span>
                        )}
                        <div className="input-container">
                          <select className="form-control pl-5" name="business_type" onChange={handleChange} onBlur={handleBlur} value={values.business_type}>
                          <option value="">Select</option>
                          <option value="Ltd">Ltd</option>
                          <option value="Sole Trader">Sole Trader</option>
                          <option value="Charity">Charity</option>
                        </select>
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Registration Number</label>
                        {errors.registration_number && touched.registration_number && (
                          <span className="text-red ms-1">
                            {errors.registration_number}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="registration_number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.registration_number}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Website</label>
                        {errors.website && touched.website && (
                          <span className="text-red ms-1">
                            {errors.website}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="website"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.website}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-3 col-md-3 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Slug</label>
                        {errors.slug && touched.slug && (
                          <span className="text-red ms-1">
                            {errors.slug}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="slug"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.slug}
                            readOnly
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-3 col-md-3" style={{ lineHeight: '7' }}> <span>.bookinclass.co.uk</span></div>


                    
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Stripe Account ID</label>
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="stripe_account_id"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.stripe_account_id}
                            readOnly
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                   
                    <div className="col-sm-12 col-md-12">
                      <h5>Primary Contact Information</h5>
                    </div>
                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Full Name</label>
                        {errors.full_name && touched.full_name && (
                          <span className="text-red ms-1">
                            {errors.full_name}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="full_name"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.full_name}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Job Title</label>
                        {errors.job_role && touched.job_role && (
                          <span className="text-red ms-1">
                            {errors.job_role}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="job_role"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.job_role}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Email Address</label>
                        {errors.email && touched.email && (
                          <span className="text-red ms-1">
                            {errors.email}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="email"
                            className="form-control pl-5"
                            name="email"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.email}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Phone Number</label>
                        {errors.phone_number && touched.phone_number && (
                          <span className="text-red ms-1">
                            {errors.phone_number}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="phone_number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.phone_number}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>


                    <div className="col-sm-12 col-md-12">
                      <h5>Business Address</h5>
                    </div>
                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Address Line 1</label>
                        {errors.address_line1 && touched.address_line1 && (
                          <span className="text-red ms-1">
                            {errors.address_line1}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="address_line1"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.address_line1}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Address Line 2</label>
                        {errors.address_line2 && touched.address_line2 && (
                          <span className="text-red ms-1">
                            {errors.address_line2}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="address_line2"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.address_line2}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">County</label>
                        <select 
                          className="form-control pl-5"
                          value={values.county} 
                          onChange={(e) => {
                            const selectedCounty = e.target.value;
                            setFieldValue('county', selectedCounty);
                            handleCountyChange(e);
                            // Add any other logic here if needed (like updating cities, etc.)
                          }}
                        >
                          <option value="">Select County</option>
                          {Object.keys(countyCityMap).sort().map(county => (
                            <option key={county} value={county}>{county}</option>
                          ))}
                        </select>
                        {errors.county && touched.county && (
                          <span className="text-red ms-1">
                            {errors.county}
                          </span>
                        )}
                        <div className="input-container ">
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">City</label>
                        <select 
                          className="form-control pl-5"
                          value={values.city || (showManualInput ? "manual" : "")} 
                          onChange={(e) => {
                            const selectedCity = e.target.value;
                            setFieldValue('city', selectedCity);
                            handleCityChange(e);
                            // Add any other logic here if needed (like updating cities, etc.)
                          }}
                        >
                          <option value="">Select City/Town</option>
                          {selectedCounty && countyCityMap[selectedCounty]?.sort().map(city => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                          <option value="manual">My town/village isn't listed</option>
                        </select>
                        {showManualInput && (
                          <div className="mt-4">
                            <label>Enter City/Town:</label>
                            <input
                              type="text"
                              placeholder="Enter your town or village"
                              value={manualCity}
                              onChange={(e) => setManualCity(e.target.value)}
                            />
                          </div>
                        )}
                        {errors.city && touched.city && (
                          <span className="text-red ms-1">
                            {errors.city}
                          </span>
                        )}
                        <div className="input-container">
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Country</label>
                        <select className="form-control pl-5" value={values.country} >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                        {errors.country && touched.country && (
                          <span className="text-red ms-1">
                            {errors.country}
                          </span>
                        )}
                        <div className="input-container">
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-6 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Postcode</label>
                        {errors.postcode && touched.postcode && (
                          <span className="text-red ms-1">
                            {errors.postcode}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="postcode"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.postcode}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-12 col-md-12">
                      <h5>Business Operation</h5>
                    </div>
                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Services Offered</label>
                        {errors.services && touched.services && (
                          <span className="text-red ms-1">
                            {errors.services}
                          </span>
                        )}
                        <div className="input-container">
                        <Select
                            isMulti
                            options={[
                              { value: "Martial Arts", label: "Martial Arts" },
                              { value: "Holiday Clubs", label: "Holiday Clubs" },
                              { value: "Tuition", label: "Tuition" },
                              { value: "Sports Coaching", label: "Sports Coaching" },
                              { value: "After School Clubs", label: "After School Clubs" },
                              { value: "Other", label: "Other" }
                            ]}
                            value={
                              values.services.map(service => ({
                                value: service,
                                label: service
                              }))
                            }
                            className="select2-container form-control"
                            classNamePrefix="select2"
                            onChange={(selectedOptions) => {
                              setFieldValue("services", selectedOptions.map(option => option.value), { shouldValidate: true });
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Target Age Range</label>
                        {errors.target_age && touched.target_age && (
                          <span className="text-red ms-1">
                            {errors.target_age}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="target_age"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.target_age}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Estimated Weekly Bookings</label>
                        {errors.no_of_weekly_bookings && touched.no_of_weekly_bookings && (
                          <span className="text-red ms-1">
                            {errors.no_of_weekly_bookings}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="no_of_weekly_bookings"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.no_of_weekly_bookings}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Typical Operating Hours</label>
                        {errors.operating_hours && touched.operating_hours && (
                          <span className="text-red ms-1">
                            {errors.operating_hours}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="operating_hours"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.operating_hours}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>


                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Booking Cancellation Policy</label>
                        {errors.booking_cancellation_policy && touched.booking_cancellation_policy && (
                          <span className="text-red ms-1">
                            {errors.booking_cancellation_policy}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="file"
                            name="booking_cancellation_policy"
                            onChange={(e) => {
                              if (e.target.files.length > 0) {
                                const file = e.target.files[0];
                                setSelectedPolicy(file);
                              }
                            }}
                            onBlur={handleBlur}
                          />
                        </div>
                        {details?.booking_cancellation_policy
                        ? (
                            <a
                              href={details.booking_cancellation_policy}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View PDF
                            </a>
                          )
                        : ""
                      }
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Custom Features or Notes</label>
                        {errors.notes && touched.notes && (
                          <span className="text-red ms-1">
                            {errors.notes}
                          </span>
                        )}
                        <div className="input-container">
                          <textarea
                            type="text"
                            className="form-control"
                            name="notes"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.notes}
                            rows="20"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Pricing Structure</label>
                        {errors.pricing_structure && touched.pricing_structure && (
                          <span className="text-red ms-1">
                            {errors.pricing_structure}
                          </span>
                        )}
                        <div className="input-container">
                          <select 
                            name="pricing_structure" 
                            className="form-control pl-5" 
                            value={values.pricing_structure}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">-- Select a Pricing Structure --</option>
                            <option value="Flate Rate">Flate Rate</option>
                            <option value="Per Session">Per Session</option>
                            <option value="Tiered">Tiered</option>
                          </select>
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6 mt-4 mb-4">
                      <div className="form-group">
                        <label className="fw-bold">Brand Colour(HEX Code)</label>
                        {errors.brand_color && touched.brand_color && (
                          <span className="text-red ms-1">
                            {errors.brand_color}
                          </span>
                        )}
                        <div className="input-container">
                          <input
                            type="text"
                            className="form-control pl-5"
                            name="brand_color"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.brand_color}
                          />
                          <img
                            src="assets/images/event-name.png"
                            className="input-img"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>


                    <div className="col-sm-6 col-md-6 text-center">
                      <button
                        type="submit"
                        className="btn btn-save"
                        disabled={submitting}
                      >
                        {submitting ? <Loader /> : "Done"}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </article>
  );
}

export default EditCompany;
